import React from 'react';

import {criteriaFromRubric, rubricWithCriteria, toPlainRubric} from 'lib/rubric/criteria';

import GuessCorrectnessBanner from '../shared/ContentBlocks/GuessCorrectnessBanner';
import type {GuessStatus} from '../shared/ContentBlocks/GuessCorrectnessBanner';
import GradedEvaluationBody from '../shared/FrqEvaluationSummary/GradedEvaluationBody.react';
import type {ManualEvaluation} from '../shared/FrqEvaluationSummary/FrqEvaluationSummary.types';

type Props = {
  /** The question's rubric as authored — an Immutable Map in app code, plain in tests. */
  rubric: unknown;
  /** The student's released evaluation, straight off their own guess. */
  evaluation: ManualEvaluation;
};

function sumScored(evaluation: ManualEvaluation): number {
  return evaluation.criteriaScores.reduce(
    (total, criterion) => total + (typeof criterion.score === 'number' ? criterion.score : 0),
    0
  );
}

/**
 * A student's released FRQ grade: the correctness banner, then the per-criterion breakdown with
 * their teacher's feedback.
 *
 * Renders through the same `GradedEvaluationBody` as the teacher's preview tab, so "preview what
 * the student will see" stays true by construction rather than by two implementations agreeing.
 */
export default function ReleasedGradeView({rubric, evaluation}: Props) {
  const criteria = criteriaFromRubric(toPlainRubric(rubric));
  const max = criteria.reduce((total, criterion) => total + criterion.maxScore, 0);
  // A directly-set overall score is the teacher's override and wins; otherwise the criteria sum is
  // the grade, which is how the server derived it at release.
  const earned = evaluation.overallScore ?? sumScored(evaluation);

  const status: GuessStatus =
    max > 0 && earned >= max ? 'correct' : earned === 0 ? 'incorrect' : 'partiallyCorrect';

  return (
    <div data-testid='student-released-grade'>
      <GradedEvaluationBody
        rubric={criteria.length > 0 ? rubricWithCriteria(criteria) : null}
        evaluation={evaluation}
        banner={
          <GuessCorrectnessBanner
            partialCreditEnabled
            overriddenGuessStatus={status}
            overriddenGrade={max > 0 ? earned / max : 0}
            className='mb-x0!'
          />
        }
      />
    </div>
  );
}
